import confetti from 'canvas-confetti';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Sound from '../../../lib/sound.js';
import Keyframes from "../../Keyframes.jsx";
import Fish from "../Fish/Fish.jsx";
import styles from "./AnimatedFish.module.scss";
import { resolveHorizontalAnimationEnd, resolveHorizontalAnimationIteration } from "./animationEnd.js";
import FishContextMenu from "./FishContextMenu/FishContextMenu.jsx";
import {
    generateFacingScaleKeyframes,
    generateFishAnimationKeyframes,
    generateHorizontalAnimationKeyframes,
    generateVerticalAnimationKeyframes,
    MIN_FISH_WIDTH_PX,
    random,
    randomSwimScale,
} from "./swimKeyframes.js";

function AnimatedFish({ fileUrl, maskUrl, focusable, isNew }){

    const width = useMemo (() => random(MIN_FISH_WIDTH_PX, 250), []);
    const [focused, setFocused] = useState(false);
    const [_isNew, setIsNew] = useState(isNew);
    const [lap, setLap] = useState(0);

    const verticalAnimationName = useMemo (() => `fish-animation-vertical-${Date.now()}-${lap}`, [lap]);
    const horizontalAnimationName = useMemo (() => `fish-animation-horizontal-${Date.now()}-${lap}`, [lap]);
    const facingAnimationName = useMemo (() => `fish-animation-facing-${Date.now()}-${lap}`, [lap]);
    const FishAnimationName = useMemo (() => `fish-animation-${Date.now()}-${lap}`, [lap]);
    const verticalAnimationKeyframes  = useMemo(() => generateVerticalAnimationKeyframes(), [lap]);
    const direction = useMemo(() => Math.random() > 0.5 ? "left" : "right", [lap]);
    const horizontalAnimationKeyframes  = useMemo(() => generateHorizontalAnimationKeyframes(width, direction), [width, direction, lap]);
    const facingAnimationKeyframes = useMemo(() => generateFacingScaleKeyframes(direction), [direction, lap]);
    const fishAnimationKeyframes  = useMemo(() => generateFishAnimationKeyframes(), [lap]);
    const horizontalAnimationDuration = useMemo (() => random(Math.pow(window.innerWidth / 350, 1.8) + 6, Math.pow(window.innerWidth / 350, 1.8) + 12), [lap]);
    const swimTop = useMemo (() => `${random(0,80)}%`, [lap]);
    const swimScale = useMemo (() => randomSwimScale(width), [lap, width]);
    const fishStyle = useMemo(() => ({
        animationName: FishAnimationName,
        animationDuration: `${random(1,4)}s`,
        animationIterationCount: "infinite",
        animationDirection: "alternate",
        animationTimingFunction: "ease-in-out",
        width,
        position: "relative"
    }), [FishAnimationName, width, lap]);
    const verticalStyle = useMemo(() => ({
        animationName: verticalAnimationName,
        animationDuration: `${random(3,5)}s`,
        animationIterationCount: "infinite",
        animationDirection: "alternate",
        animationTimingFunction: "ease-in-out",
        width,
        scale: `${swimScale}`
    }), [verticalAnimationName, width, lap, swimScale]);
    const horizontalStyle = useMemo(() => ({
        animationName: horizontalAnimationName,
        animationDuration: `${horizontalAnimationDuration}s`,
        animationIterationCount: "infinite",
        animationDirection: "normal",
        animationTimingFunction: "linear",
        top: swimTop,
        left: direction === "left" ? "100%" : "auto",
        right: direction === "right" ? "100%" : "auto",
        width
    }), [horizontalAnimationDuration, horizontalAnimationName, direction, width, swimTop]);
    const facingStyle = useMemo(() => ({
        animationName: facingAnimationName,
        animationDuration: `${horizontalAnimationDuration}s`,
        animationIterationCount: "infinite",
        animationDirection: "normal",
        animationTimingFunction: "linear",
        transformOrigin: "center",
    }), [facingAnimationName, horizontalAnimationDuration]);
    const animations = useMemo(() => [
        {animationName: horizontalAnimationName, keyframes: horizontalAnimationKeyframes},
        {animationName: facingAnimationName, keyframes: facingAnimationKeyframes},
        {animationName: verticalAnimationName, keyframes: verticalAnimationKeyframes},
        {animationName: FishAnimationName, keyframes: fishAnimationKeyframes}
    ], [horizontalAnimationName, horizontalAnimationKeyframes, facingAnimationName, facingAnimationKeyframes, verticalAnimationName, verticalAnimationKeyframes, FishAnimationName, fishAnimationKeyframes]);

    const startNextLap = useCallback((event) => {
        const { clearIsNew, nextLap } = resolveHorizontalAnimationEnd(event, {
            isNew: _isNew,
            horizontalAnimationName,
        });
        if (clearIsNew) setIsNew(false);
        if (nextLap) setLap(n => n + 1);
    }, [_isNew, horizontalAnimationName]);

    const horizontalAnimationIteration = useCallback((event) => {
        const { nextLap } = resolveHorizontalAnimationIteration(event, { horizontalAnimationName });
        if (nextLap) setLap(n => n + 1);
    }, [horizontalAnimationName]);

    const fishOnClick = useCallback(() => {
        if (!focusable) return;
        Sound.waterDrop();
        if (focused) setFocused(false);
        else setFocused(true);
    }, [fileUrl, focusable, focused])

    //confetti
    useEffect (() => {
        if (!isNew) return;
        const y = parseFloat(String(horizontalStyle.top).replace('%', '')) / 100;
        const left = !!horizontalAnimationKeyframes["0%"]["left"]

        const t = setTimeout(() => 
            confetti({
                particleCount: 100,
                spread: 30,
                origin: { y, x: left ? 1 : 0 },
                angle:  left ?  130 : 50,
            })
        , 1500);

        return () => clearTimeout(t)

    }, [isNew]);

    return (
        <div className={`${styles.fishContainer} ${focusable ? styles.clickable : ''} ${focused ? styles.focused : ''}`}>
            <Keyframes 
                animations={animations}
            >
                <div
                    className={styles.horizontalMovement}
                    style={horizontalStyle}
                    onAnimationEnd={startNextLap}
                    onAnimationIteration={horizontalAnimationIteration}
                >
                    <div className={styles.verticalMovement} style={verticalStyle}>
                        <div style={fishStyle}>
                            <div className={`${_isNew ? `${styles.shine} ${styles.isNew}` : ''}`}>
                                <div style={facingStyle}>
                                    <Fish fileUrl={fileUrl} maskUrl={maskUrl} width={width} fishOnClick={fishOnClick}/>
                                </div>
                            </div>
                            {!focusable ? null :
                                <div className={`${styles.contextMenu} ${focused ? styles.showContextMenu : ''}`}>
                                    <FishContextMenu fileUrl={fileUrl} />
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </Keyframes>
        </div>
    );
}

export default AnimatedFish;
